import React, { useCallback, useEffect, useState } from 'react';
import { useLocalization } from '@/services/localizationService';
import noneIcon from '@/public/images/icons/none.svg';
import { EditorPipelineStep } from '@/services/editorService';
import { useRecoilValue } from 'recoil';
import { mediaShownInComparerState } from '@/stores/mediaStore';
import { SectionName } from '@/services/settingsService/aiTypes';
import { useSubscriptionUpgradePrompts } from '@/features/upgradeSubscription/useSubscriptionUpgradePrompts';
import { UserAction } from '@/services/userService';
import diamondIcon from '@/public/images/icons/diamond.svg';
import { useTracker } from '@/core/tracking';
import equal from 'fast-deep-equal';
import { EditorPreview } from './editorPreviewsTypes';

export default function EditorPreviews({
  aiToolbarSection,
  pipelineStep,
  previews,
  lastAppliedModel,
  onRemovePipelineStep,
  onSelectPreview,
  shouldBlockUser,
  isBusinessOnly,
}: {
  aiToolbarSection: SectionName;
  pipelineStep: EditorPipelineStep;
  previews: EditorPreview[];
  lastAppliedModel?: object;
  onRemovePipelineStep: ({
    pipelineStep,
  }: {
    pipelineStep: EditorPipelineStep;
  }) => void;
  onSelectPreview: ({
    model,
    pipelineStep,
  }: {
    model: object;
    pipelineStep: EditorPipelineStep;
  }) => void;
  shouldBlockUser: boolean;
  isBusinessOnly?: boolean;
}) {
  const mediaInComparer = useRecoilValue(mediaShownInComparerState);
  const { t, data } = useLocalization();
  const { track } = useTracker();

  const {
    showCTAOnPremiumAIModelsSelection,
    showCTAOnBusinessAIModelsSelection,
  } = useSubscriptionUpgradePrompts();

  const [selected, setSelected] = useState<object>();
  useEffect(
    () => setSelected(lastAppliedModel),
    [lastAppliedModel, mediaInComparer.id]
  );

  const onClick = useCallback(
    (model?: object) => {
      if (shouldBlockUser && typeof model !== 'undefined') {
        const previousAction = UserAction.AiModelsVariantClick;
        track(`${previousAction}_cta_popup`, 'shown', {
          chosenModel: model,
        });

        if (isBusinessOnly) {
          showCTAOnBusinessAIModelsSelection({
            aiToolbarSection,
            previousAction,
          });
        } else {
          showCTAOnPremiumAIModelsSelection({
            aiToolbarSection,
            previousAction,
          });
        }

        return;
      }

      setSelected(model);
      if (!model) {
        onRemovePipelineStep({ pipelineStep });
      } else {
        onSelectPreview({ model, pipelineStep });
      }
    },
    [
      aiToolbarSection,
      isBusinessOnly,
      onRemovePipelineStep,
      onSelectPreview,
      pipelineStep,
      shouldBlockUser,
      showCTAOnBusinessAIModelsSelection,
      showCTAOnPremiumAIModelsSelection,
      track,
    ]
  );

  return (
    <div className="flex flex-wrap mt-4 max-w-[220px]">
      <button
        type="button"
        onClick={() => onClick()}
        className="preview mr-[10px] mb-[10px]"
      >
        <div
          className={`flex bg-gray-450 rounded-[6px] w-[66px] h-[66px] border-2 justify-center ${
            !selected ? 'border-gray-200' : 'border-transparent'
          }`}
        >
          <img {...noneIcon} alt="none" />
        </div>
        <p className="text-[10px] leaning-[12px] mt-[5px] font-semibold text-gray-300">
          {t(data.common.none)}
        </p>
      </button>
      {previews.map((preview) => (
        <button
          key={preview.id}
          type="button"
          onClick={() => onClick(preview.value)}
          className="preview mr-[10px] mb-[10px] relative"
        >
          {shouldBlockUser && (
            <img
              {...diamondIcon}
              className="top-1 end-1 absolute w-3.5 h-3.5 z-30"
              alt=""
            />
          )}
          <img
            src={preview.url}
            alt={preview.id}
            className={`object-cover w-[66px] h-[66px] rounded-[6px] border-2 ${
              equal(selected, preview.value)
                ? 'border-gray-200'
                : 'border-transparent'
            } ${shouldBlockUser ? 'opacity-60' : ''}`}
          />
          <p className="text-[10px] leaning-[12px] mt-[5px] font-semibold text-gray-300">
            {preview.label}
          </p>
        </button>
      ))}
    </div>
  );
}
